import React, { useReducer } from 'react';

import uniqueId from 'lodash/uniqueId';

import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiChevronRight, mdiArrowLeft, mdiClose } from '@lumapps/lumx/icons';
import {
    Button,
    Dialog,
    Emphasis,
    Toolbar,
    Size,
    IconButton,
    FlexBox,
    Orientation,
    Message,
    Icon,
    Kind,
    DialogProps,
} from '@lumapps/lumx/react';
import { isSectionNode, NavigationMapItem } from '@lumapps/navigation/types';
import { GLOBAL, useTranslate, translateFromApiV2Format, TranslatableObject } from '@lumapps/translations';
import { moveSubtree } from '@lumapps/utils/tree/moveSubtree';
import { Direction } from '@lumapps/utils/types/Direction';

import { getNavigationMapNodeTitle } from '../../utils/nodes';
import { EditNodeDialog } from '../EditNodeDialog';
import { NavigationListNode } from '../NavigationListNode';
import { reducer, actions, initNodeDestinationNodeDialogState, Modes } from './slice';

import './index.scss';

type TextFromSelectedNodeFunction = (options: { selectedNode: NavigationMapItem | null; mode: Modes }) => string;

export interface SetNodeDestinationDialogProps {
    /** whether the dialog should be open or not */
    isOpen: boolean;
    /** callback to be executed once the dialog is closed */
    onClose: () => void;
    /** callback to be executed with the new navigation as a parameter */
    onSave: (nodes: NavigationMapItem[], destination?: NavigationMapItem | null) => void;
    /** node that will be moved to a new section */
    nodeToMove: NavigationMapItem;
    /** current list of nodes. */
    nodes: NavigationMapItem[];
    /** whether the save button is disabled or not */
    isSaveDisabled?: (
        depth: number,
        destination: NavigationMapItem | null,
    ) => { isDisabled: boolean; message?: string };
    /** scope for tracking purposes */
    scope: string;
    /** reference to the element that has triggered the modal */
    buttonRef?: DialogProps['parentElement'];
    /** Whether the node can accept children or not */
    isListNode?: (node: NavigationMapItem) => boolean;
    /** Override the title of the dialog */
    dialogTitle?: string | TextFromSelectedNodeFunction;
    /** Override the save button text of the dialog */
    saveButtonTitle?: string | TextFromSelectedNodeFunction;
    /** current default language */
    defaultLanguage: string;
    /** list of the platform's languages */
    languages: string[];
    /** Show the dialog as loading */
    isLoading?: boolean;
}

const CLASSNAME = 'set-node-destination-dialog';

/**
 * Modal that will display a navigation on a folder view fashion, allowing to move a given node
 * into a selected section. The callback `onSave` will return the new updated navigation once the
 * confirmation button is clicked.
 * @param SetNodeDestinationDialogProps
 */
export const SetNodeDestinationDialog: React.FC<SetNodeDestinationDialogProps> = ({
    isOpen,
    onClose,
    nodes,
    nodeToMove,
    onSave,
    isSaveDisabled,
    isListNode = isSectionNode,
    scope,
    buttonRef,
    dialogTitle,
    saveButtonTitle,
    defaultLanguage,
    languages,
    isLoading,
}) => {
    /** Constants */
    const labelId = React.useMemo(() => uniqueId(`${CLASSNAME}-label-`), []);
    const contentElement = React.useRef(null);
    const newItemRef = React.useRef<HTMLDivElement>(null);

    /** Hooks */
    const { get } = useDataAttributes(scope);
    const { translateKey, translateObject, translateAndReplace } = useTranslate();

    /** State management */
    const [state, dispatch] = useReducer(reducer, { nodes, nodeToMove }, initNodeDestinationNodeDialogState);
    const { displayedNodes, mode, newPosition, depth, selectedNode, isEditOpen, nodeToMove: updatedNodeToMove } = state;

    const { isDisabled, message } = isSaveDisabled
        ? isSaveDisabled(depth, selectedNode)
        : { isDisabled: false, message: null };

    /**
     * Callback when a node should be moved up or down the current list
     */
    const handleMoveNode = (node: NavigationMapItem, direction: Direction) => {
        dispatch(actions.moveNode({ node, direction }));
    };

    /**
     * Callback when the destination has been selected.
     */
    const onSelectDestination = () => {
        dispatch(actions.selectDestination());
    };

    /** Focus the newly added element */
    React.useEffect(() => {
        const ref = newItemRef.current;
        if (ref) {
            // Get the first focusable element
            const buttons = ref.querySelectorAll('button');
            const firstButton = buttons[0];
            if (firstButton) {
                firstButton.focus();
            }
        }
    }, [mode, translateKey]);

    /**
     * Callback when a node has been requested to be deleted.
     */
    const handleDeleteNode = (path: Array<NavigationMapItem['id']>) => {
        dispatch(actions.removeNode(path[0]));
    };

    /**
     * Callback when the node has successfully been edited.
     */
    const handleEditNodeSave = (newNode: NavigationMapItem) => {
        dispatch(actions.updateNode(newNode));
    };

    const toggleEdit = () => {
        dispatch(actions.toggleEdition());
    };

    /**
     * Callback to be executed once the user has chosen the section and position that the node will be moved to.
     */
    const handleConfirmPosition = () => {
        const newParent = selectedNode?.id;
        const updatedNodes = moveSubtree(nodes, updatedNodeToMove, newParent, newPosition);
        onSave(updatedNodes, selectedNode);
    };

    const title = translateObject(translateFromApiV2Format((updatedNodeToMove.title || {}) as TranslatableObject));

    const generatedDialogTitle = React.useMemo(() => {
        if (dialogTitle) {
            return typeof dialogTitle === 'string' ? dialogTitle : dialogTitle({ mode, selectedNode });
        }
        return translateAndReplace(GLOBAL.MOVE_SOMETHING, {
            ENT: title || '',
        });
    }, [dialogTitle, mode, selectedNode, title, translateAndReplace]);

    const generatedSaveButton = React.useMemo(() => {
        if (saveButtonTitle) {
            return typeof saveButtonTitle === 'string' ? saveButtonTitle : saveButtonTitle({ mode, selectedNode });
        }

        if (mode === 'destination') {
            return translateKey(GLOBAL.MOVE_HERE);
        }

        return translateKey(GLOBAL.DONE);
    }, [mode, saveButtonTitle, selectedNode, translateKey]);

    const selectedNodeTitle = selectedNode ? getNavigationMapNodeTitle(selectedNode, defaultLanguage) : undefined;

    return (
        <Dialog
            isOpen={isOpen}
            size={Size.regular}
            className={CLASSNAME}
            dialogProps={{ 'aria-labelledby': labelId }}
            parentElement={buttonRef}
            /**
             * As this dialog has lots of cases, the easier solution is to simply set the
             * focus on the content on open.
             */
            focusElement={contentElement}
            onClose={onClose}
            isLoading={isLoading}
        >
            <header>
                <Toolbar
                    className={`${CLASSNAME}__toolbar`}
                    label={
                        <h1 id={labelId} className="lumx-typography-title">
                            {generatedDialogTitle}
                        </h1>
                    }
                    after={
                        <IconButton
                            className="lumx-spacing-margin-right-regular"
                            emphasis={Emphasis.low}
                            icon={mdiClose}
                            onClick={onClose}
                            label={translateKey(GLOBAL.CLOSE)}
                        />
                    }
                />
            </header>
            <div ref={contentElement} tabIndex={-1} className={`${CLASSNAME}__content`}>
                {selectedNode ? (
                    <FlexBox orientation={Orientation.horizontal} className="lumx-spacing-margin-bottom-big">
                        <IconButton
                            className="lumx-spacing-margin-right-regular"
                            label={translateKey(GLOBAL.MORE)}
                            icon={mdiArrowLeft}
                            emphasis={Emphasis.medium}
                            hasBackground
                            size={Size.m}
                            isDisabled={mode === 'position'}
                            onClick={(e: any) => {
                                e.stopPropagation();
                                dispatch(actions.selectParentNode());
                            }}
                        />
                        {selectedNodeTitle && (
                            <h2 className={`${CLASSNAME}__selected-node`}>
                                {translateObject(translateFromApiV2Format(selectedNodeTitle))}
                            </h2>
                        )}
                    </FlexBox>
                ) : null}

                {message ? (
                    <Message hasBackground kind={Kind.info} className="lumx-spacing-margin-bottom">
                        {message}
                    </Message>
                ) : null}

                {displayedNodes
                    ? displayedNodes.map((node, index) => {
                          /**
                           * When selecting the destination, we prevent displaying the node that we are actually trying to move in order to avoid
                           * selecting while browsing the different sections.
                           */
                          if (node.id === nodeToMove.id && mode === 'destination') {
                              return null;
                          }

                          /** When we are in "position" mode, we display the node with its actions */
                          if (node.id === nodeToMove.id && mode === 'position') {
                              return (
                                  <NavigationListNode
                                      key={node.id}
                                      node={node}
                                      path={[node.id as string]}
                                      className="lumx-spacing-margin-bottom"
                                      scope={scope}
                                      onMoveNode={handleMoveNode}
                                      onDeleteNode={handleDeleteNode}
                                      onEditNode={toggleEdit}
                                      disableExpansion
                                      ref={newItemRef}
                                      isFirst={index === 0}
                                      isLast={index === displayedNodes.length - 1}
                                  />
                              );
                          }

                          return (
                              <NavigationListNode
                                  key={node.id}
                                  node={node}
                                  path={[node.id as string]}
                                  hidePreviewLink
                                  disableExpansion
                                  className="lumx-spacing-margin-bottom"
                                  isDisabled={!isListNode?.(node) || mode === 'position'}
                                  scope={scope}
                                  onClickNode={() => {
                                      if (isListNode?.(node) && mode === 'destination') {
                                          dispatch(actions.selectNode(node));
                                      }
                                  }}
                                  isHoverable={isListNode?.(node) && mode === 'destination'}
                                  actions={
                                      // We only want to display the action for those nodes that are sections.
                                      isListNode?.(node) && mode === 'destination' ? (
                                          <Icon label={translateKey(GLOBAL.OPEN)} icon={mdiChevronRight} />
                                      ) : null
                                  }
                              />
                          );
                      })
                    : null}
            </div>
            <footer>
                <Toolbar
                    after={
                        <>
                            <Button
                                emphasis={Emphasis.medium}
                                onClick={onClose}
                                {...get({
                                    element: 'move-node',
                                    action: 'cancel',
                                })}
                            >
                                {translateKey(GLOBAL.CANCEL)}
                            </Button>
                            <Button
                                isDisabled={isDisabled}
                                onClick={mode === 'destination' ? onSelectDestination : handleConfirmPosition}
                                className="lumx-spacing-margin-left-regular"
                                {...get({
                                    element: 'move-node',
                                    action: 'move',
                                })}
                            >
                                {generatedSaveButton}
                            </Button>
                        </>
                    }
                />
            </footer>
            {isEditOpen && (
                <EditNodeDialog
                    scope={scope}
                    isOpen={isEditOpen}
                    nodeToEdit={updatedNodeToMove}
                    onClose={toggleEdit}
                    onEdit={handleEditNodeSave}
                    defaultLanguage={defaultLanguage}
                    languages={languages}
                />
            )}
        </Dialog>
    );
};
